/**
 * Renderer-neutral measured floor plans. Every persisted length is an integer count of
 * sixteenth-inches; screen pixels exist only in editor/view transforms.
 */

export const SIXTEENTHS_PER_INCH = 16;
export const SIXTEENTHS_PER_FOOT = 12 * SIXTEENTHS_PER_INCH;

export type FloorPlanPoint = { x: number; y: number; };
export type FloorPlanClearance = { front: number; back: number; side: number; };
export type FloorPlanTable = {
  id: string;
  number: string;
  center: FloorPlanPoint;
  width: number;
  depth: number;
  /** Clockwise degrees; zero faces toward positive y. */
  rotation: number;
  clearance?: FloorPlanClearance;
};
/** Where a table sits and how big it is, without what only a full-size plan shows. */
export type FloorPlanTableFootprint = Pick<
  FloorPlanTable,
  "id" | "center" | "width" | "depth" | "rotation"
>;
export type FloorPlanArea = {
  id: string;
  name: string;
  obstructs: boolean;
  polygon: FloorPlanPoint[];
};
export type FloorPlanDocument = {
  version: 1;
  unit: "sixteenth-inch";
  name: string;
  boundary: FloorPlanPoint[];
  areas: FloorPlanArea[];
  tables: FloorPlanTable[];
  defaults: {
    clearance: FloorPlanClearance;
    table: { width: number; depth: number; };
  };
  /** Calibrated source metadata only; the imported bytes stay behind the storage port. */
  underlay?: FloorPlanUnderlayMetadata;
};

/**
 * The part of a plan a thumbnail draws: the room, what obstructs it, and where the tables are.
 *
 * A list of layouts carries this instead of the plans themselves, because a table's number, its
 * clearances, an area's label and the walkable areas are none of them legible at that size, and a
 * traced hall's plan is two thirds larger than its outline. A screen that draws one layout full
 * size fetches the document.
 */
export type FloorPlanPreview = {
  boundary: FloorPlanPoint[];
  obstructions: { id: string; polygon: FloorPlanPoint[]; }[];
  tables: FloorPlanTableFootprint[];
};

/** Projects a whole plan down to what a thumbnail draws, matching what the server sends. */
export function floorPlanPreview(document: FloorPlanDocument): FloorPlanPreview {
  return {
    boundary: document.boundary,
    obstructions: document.areas
      .filter((area) => area.obstructs)
      .map(({ id, polygon }) => ({ id, polygon })),
    tables: document.tables.map(({ id, center, width, depth, rotation }) => ({
      id,
      center,
      width,
      depth,
      rotation,
    })),
  };
}

const inches = (value: number) => value * SIXTEENTHS_PER_INCH;
const feet = (value: number) => value * SIXTEENTHS_PER_FOOT;

/**
 * A 6ft x 3ft banquet table. The depth is a whole number of feet on purpose: a table's edges sit
 * at `center +/- depth/2`, so only a whole-foot depth can put both edges on the one-foot grid, and
 * let an Organizer sit a table flush against a wall or behind the row in front (TRO-546).
 */
export const BANQUET_TABLE = { width: feet(6), depth: feet(3) } as const;
export const DEFAULT_CLEARANCE: FloorPlanClearance = {
  front: inches(48),
  back: inches(36),
  side: inches(3),
};

export const AREA_PRESETS: readonly { name: string; obstructs: boolean | null; }[] = [
  { name: "Stage", obstructs: true },
  { name: "Pillar", obstructs: true },
  { name: "Loading bay", obstructs: true },
  { name: "Storage", obstructs: true },
  { name: "Bar", obstructs: true },
  { name: "Entrance", obstructs: false },
  { name: "Exit", obstructs: false },
  { name: "Step-free entrance", obstructs: false },
  { name: "Restrooms", obstructs: false },
  { name: "Accessible restroom", obstructs: false },
  { name: "Registration", obstructs: false },
  { name: "Food", obstructs: false },
  { name: "ATM", obstructs: false },
  { name: "Seating", obstructs: false },
  { name: "Artist Alley", obstructs: false },
  { name: "Other", obstructs: null },
];

export function createFloorPlan(name: string, boundary: FloorPlanPoint[] = []): FloorPlanDocument {
  return {
    version: 1,
    unit: "sixteenth-inch",
    name,
    boundary,
    areas: [],
    tables: [],
    defaults: {
      clearance: { ...DEFAULT_CLEARANCE },
      table: { ...BANQUET_TABLE },
    },
  };
}

const FRACTIONS = [
  "",
  "¹⁄₁₆",
  "⅛",
  "³⁄₁₆",
  "¼",
  "⁵⁄₁₆",
  "⅜",
  "⁷⁄₁₆",
  "½",
  "⁹⁄₁₆",
  "⅝",
  "¹¹⁄₁₆",
  "¾",
  "¹³⁄₁₆",
  "⅞",
  "¹⁵⁄₁₆",
] as const;

/**
 * Parses a typed length into sixteenth-inches, or null. A bare number means feet, and
 * sub-sixteenth precision is rejected.
 */
export function parseLength(input: string): number | null {
  let value = input.trim().replace(/[’′]/g, "'").replace(/[”″]/g, '"');
  for (let sixteenths = FRACTIONS.length - 1; sixteenths > 0; sixteenths -= 1) {
    value = value.replaceAll(FRACTIONS[sixteenths], ` ${sixteenths}/16`);
  }
  if (!value) return null;

  const feetMatch = /^(\d+(?:\.\d+)?)\s*'(?:\s*(.*?)\s*")?$/.exec(value);
  if (feetMatch) {
    const footPart = exactUnits(Number(feetMatch[1]) * SIXTEENTHS_PER_FOOT);
    const inchPart = feetMatch[2] ? parseInches(feetMatch[2]) : 0;
    return footPart === null || inchPart === null ? null : footPart + inchPart;
  }

  const inchMatch = /^(.*?)\s*"$/.exec(value);
  if (inchMatch) return parseInches(inchMatch[1]);
  return exactUnits(Number(value) * SIXTEENTHS_PER_FOOT);
}

function exactUnits(value: number): number | null {
  if (!Number.isFinite(value) || value < 0 || Math.abs(value - Math.round(value)) > 1e-8) {
    return null;
  }
  return Math.round(value);
}

function parseInches(value: string): number | null {
  const match = /^(?:(\d+(?:\.\d+)?)\s*)?(?:(\d+)\s*\/\s*(\d+))?$/.exec(value.trim());
  if (!match || (!match[1] && !match[2])) return null;
  const whole = Number(match[1] ?? 0);
  const numerator = Number(match[2] ?? 0);
  const denominator = Number(match[3] ?? 1);
  if (!Number.isFinite(whole) || denominator <= 0 || numerator >= denominator) return null;
  return exactUnits((whole + numerator / denominator) * SIXTEENTHS_PER_INCH);
}

export function formatLength(length: number): string {
  if (!Number.isInteger(length) || length < 0) {
    throw new TypeError("length must be a non-negative integer");
  }
  const wholeFeet = Math.floor(length / SIXTEENTHS_PER_FOOT);
  const remainder = length % SIXTEENTHS_PER_FOOT;
  const wholeInches = Math.floor(remainder / SIXTEENTHS_PER_INCH);
  const fraction = remainder % SIXTEENTHS_PER_INCH;
  const inchesText = wholeInches || fraction
    ? `${wholeInches || ""}${FRACTIONS[fraction]}`
    : !wholeFeet
    ? "0"
    : "";
  return [wholeFeet ? `${wholeFeet}′` : "", inchesText ? `${inchesText}″` : ""].filter(Boolean)
    .join(" ");
}

export function serializeFloorPlan(document: FloorPlanDocument): string {
  return JSON.stringify(document);
}

export class FloorPlanParseError extends Error {
  constructor(readonly paths: string[]) {
    super(`Invalid floor plan: ${paths.join(", ")}`);
    this.name = "FloorPlanParseError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Validates the persisted shape and exact integer lengths. Tables the editor would flag, such as
 * one outside the boundary, still parse.
 */
export function parseFloorPlan(json: string): FloorPlanDocument {
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    throw new FloorPlanParseError(["$"]);
  }
  const paths: string[] = [];
  if (!isRecord(value)) throw new FloorPlanParseError(["$"]);
  if (value.version !== 1) paths.push("version");
  if (value.unit !== "sixteenth-inch") paths.push("unit");
  if (typeof value.name !== "string") paths.push("name");
  checkPolygon(value.boundary, "boundary", paths);
  checkAreas(value.areas, paths);
  checkTables(value.tables, paths);
  checkDefaults(value.defaults, paths);
  if (value.underlay !== undefined) checkUnderlay(value.underlay, paths);
  if (paths.length) throw new FloorPlanParseError(paths);
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- shape is fully walked above; paths.length === 0 means every field matched.
  return value as FloorPlanDocument;
}

function checkLength(value: unknown, path: string, paths: string[], positive = false): void {
  if (!Number.isInteger(value) || (positive ? Number(value) <= 0 : Number(value) < 0)) {
    paths.push(path);
  }
}

function checkInteger(value: unknown, path: string, paths: string[]): void {
  if (!Number.isInteger(value)) paths.push(path);
}

function checkPoint(value: unknown, path: string, paths: string[]): void {
  if (!isRecord(value)) {
    paths.push(path);
    return;
  }
  checkInteger(value.x, `${path}.x`, paths);
  checkInteger(value.y, `${path}.y`, paths);
}

function checkPolygon(value: unknown, path: string, paths: string[]): void {
  if (!Array.isArray(value)) {
    paths.push(path);
    return;
  }
  value.forEach((point, index) => checkPoint(point, `${path}[${index}]`, paths));
}

function checkClearance(value: unknown, path: string, paths: string[]): void {
  if (!isRecord(value)) {
    paths.push(path);
    return;
  }
  checkLength(value.front, `${path}.front`, paths);
  checkLength(value.back, `${path}.back`, paths);
  checkLength(value.side, `${path}.side`, paths);
}

function checkAreas(value: unknown, paths: string[]): void {
  if (!Array.isArray(value)) {
    paths.push("areas");
    return;
  }
  value.forEach((area, index) => {
    const path = `areas[${index}]`;
    if (!isRecord(area)) {
      paths.push(path);
      return;
    }
    if (typeof area.id !== "string") paths.push(`${path}.id`);
    if (typeof area.name !== "string") paths.push(`${path}.name`);
    if (typeof area.obstructs !== "boolean") paths.push(`${path}.obstructs`);
    checkPolygon(area.polygon, `${path}.polygon`, paths);
  });
}

function checkTables(value: unknown, paths: string[]): void {
  if (!Array.isArray(value)) {
    paths.push("tables");
    return;
  }
  value.forEach((table, index) => {
    const path = `tables[${index}]`;
    if (!isRecord(table)) {
      paths.push(path);
      return;
    }
    if (typeof table.id !== "string") paths.push(`${path}.id`);
    if (typeof table.number !== "string") paths.push(`${path}.number`);
    checkPoint(table.center, `${path}.center`, paths);
    checkLength(table.width, `${path}.width`, paths, true);
    checkLength(table.depth, `${path}.depth`, paths, true);
    if (typeof table.rotation !== "number" || !Number.isFinite(table.rotation)) {
      paths.push(`${path}.rotation`);
    }
    if (table.clearance !== undefined) checkClearance(table.clearance, `${path}.clearance`, paths);
  });
}

function checkDefaults(value: unknown, paths: string[]): void {
  if (!isRecord(value)) {
    paths.push("defaults");
    return;
  }
  checkClearance(value.clearance, "defaults.clearance", paths);
  if (!isRecord(value.table)) {
    paths.push("defaults.table");
    return;
  }
  checkLength(value.table.width, "defaults.table.width", paths, true);
  checkLength(value.table.depth, "defaults.table.depth", paths, true);
}

function checkFinite(value: unknown, path: string, paths: string[], positive = false): void {
  if (
    typeof value !== "number" || !Number.isFinite(value) || (positive && value <= 0)
  ) paths.push(path);
}

function checkUnderlay(value: unknown, paths: string[]): void {
  if (!isRecord(value)) {
    paths.push("underlay");
    return;
  }
  if (typeof value.objectKey !== "string") paths.push("underlay.objectKey");
  if (typeof value.name !== "string") paths.push("underlay.name");
  if (typeof value.mediaType !== "string" || !UNDERLAY_MEDIA.has(value.mediaType)) {
    paths.push("underlay.mediaType");
  }
  checkFinite(value.sourceWidth, "underlay.sourceWidth", paths, true);
  checkFinite(value.sourceHeight, "underlay.sourceHeight", paths, true);
  if (value.calibrated !== true) paths.push("underlay.calibrated");
  if (!isRecord(value.calibration)) {
    paths.push("underlay.calibration");
    return;
  }
  checkSourcePoint(value.calibration.sourceOrigin, "underlay.calibration.sourceOrigin", paths);
  checkPoint(value.calibration.worldOrigin, "underlay.calibration.worldOrigin", paths);
  checkFinite(
    value.calibration.sixteenthsPerSourceUnit,
    "underlay.calibration.sixteenthsPerSourceUnit",
    paths,
    true,
  );
  const reference = value.calibration.reference;
  if (reference === undefined) return;
  if (!isRecord(reference)) {
    paths.push("underlay.calibration.reference");
    return;
  }
  checkSourcePoint(reference.from, "underlay.calibration.reference.from", paths);
  checkSourcePoint(reference.to, "underlay.calibration.reference.to", paths);
  checkLength(reference.length, "underlay.calibration.reference.length", paths, true);
  if (
    isRecord(reference.from) && isRecord(reference.to)
    && reference.from.x === reference.to.x && reference.from.y === reference.to.y
  ) paths.push("underlay.calibration.reference");
}

function checkSourcePoint(value: unknown, path: string, paths: string[]): void {
  if (!isRecord(value)) {
    paths.push(path);
    return;
  }
  checkFinite(value.x, `${path}.x`, paths);
  checkFinite(value.y, `${path}.y`, paths);
}

export function distance(a: FloorPlanPoint, b: FloorPlanPoint): number {
  return Math.round(Math.hypot(b.x - a.x, b.y - a.y));
}

export function polygonArea(polygon: readonly FloorPlanPoint[]): number {
  let twiceArea = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const point = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    twiceArea += point.x * next.y - next.x * point.y;
  }
  return Math.abs(twiceArea) / 2;
}

export type FloorPlanWall = {
  index: number;
  from: FloorPlanPoint;
  to: FloorPlanPoint;
  length: number;
  derived: boolean;
};

export function editableWalls(document: FloorPlanDocument): FloorPlanWall[] {
  return document.boundary.map((from, index) => ({
    index,
    from,
    to: document.boundary[(index + 1) % document.boundary.length],
    length: distance(from, document.boundary[(index + 1) % document.boundary.length]),
    derived: index === document.boundary.length - 1,
  }));
}

/** Slides one wall's far vertex and rigidly carries every later vertex; the closing wall absorbs it. */
export function resizeWall(
  document: FloorPlanDocument,
  index: number,
  length: number,
): FloorPlanDocument {
  if (
    !Number.isInteger(length) || length <= 0 || index < 0 || index >= document.boundary.length - 1
  ) return document;
  const from = document.boundary[index];
  const to = document.boundary[index + 1];
  const current = Math.hypot(to.x - from.x, to.y - from.y);
  if (current === 0) return document;
  const moved = {
    x: Math.round(from.x + ((to.x - from.x) / current) * length),
    y: Math.round(from.y + ((to.y - from.y) / current) * length),
  };
  const dx = moved.x - to.x;
  const dy = moved.y - to.y;
  return {
    ...document,
    boundary: document.boundary.map((point, pointIndex) =>
      pointIndex > index ? { x: point.x + dx, y: point.y + dy } : point
    ),
  };
}

function rectanglePolygon(
  center: FloorPlanPoint,
  width: number,
  depth: number,
  rotation: number,
  back = depth / 2,
  front = depth / 2,
): FloorPlanPoint[] {
  const radians = (rotation * Math.PI) / 180;
  const widthX = Math.cos(radians);
  const widthY = Math.sin(radians);
  const frontX = -Math.sin(radians);
  const frontY = Math.cos(radians);
  const at = (across: number, forward: number) => ({
    x: Math.round(center.x + across * widthX + forward * frontX),
    y: Math.round(center.y + across * widthY + forward * frontY),
  });
  return [at(-width / 2, -back), at(width / 2, -back), at(width / 2, front), at(-width / 2, front)];
}

export function tablePolygon(table: FloorPlanTableFootprint): FloorPlanPoint[] {
  return rectanglePolygon(table.center, table.width, table.depth, table.rotation);
}

export function envelopePolygon(
  table: FloorPlanTable,
  document: FloorPlanDocument,
): FloorPlanPoint[] {
  const clearance = resolvedClearance(table, document);
  return rectanglePolygon(
    table.center,
    table.width + 2 * clearance.side,
    table.depth + clearance.front + clearance.back,
    table.rotation,
    table.depth / 2 + clearance.back,
    table.depth / 2 + clearance.front,
  );
}

const EPSILON = 1e-7;

function onSegment(point: FloorPlanPoint, a: FloorPlanPoint, b: FloorPlanPoint): boolean {
  const cross = (point.y - a.y) * (b.x - a.x) - (point.x - a.x) * (b.y - a.y);
  return Math.abs(cross) <= EPSILON
    && point.x >= Math.min(a.x, b.x) - EPSILON
    && point.x <= Math.max(a.x, b.x) + EPSILON
    && point.y >= Math.min(a.y, b.y) - EPSILON
    && point.y <= Math.max(a.y, b.y) + EPSILON;
}

function orientation(a: FloorPlanPoint, b: FloorPlanPoint, c: FloorPlanPoint): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsIntersect(
  a: FloorPlanPoint,
  b: FloorPlanPoint,
  c: FloorPlanPoint,
  d: FloorPlanPoint,
): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  if (
    ((abC > EPSILON && abD < -EPSILON) || (abC < -EPSILON && abD > EPSILON))
    && ((cdA > EPSILON && cdB < -EPSILON) || (cdA < -EPSILON && cdB > EPSILON))
  ) return true;
  return (Math.abs(abC) <= EPSILON && onSegment(c, a, b))
    || (Math.abs(abD) <= EPSILON && onSegment(d, a, b))
    || (Math.abs(cdA) <= EPSILON && onSegment(a, c, d))
    || (Math.abs(cdB) <= EPSILON && onSegment(b, c, d));
}

function properIntersection(
  a: FloorPlanPoint,
  b: FloorPlanPoint,
  c: FloorPlanPoint,
  d: FloorPlanPoint,
): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  return ((abC > EPSILON && abD < -EPSILON) || (abC < -EPSILON && abD > EPSILON))
    && ((cdA > EPSILON && cdB < -EPSILON) || (cdA < -EPSILON && cdB > EPSILON));
}

export function pointInPolygon(point: FloorPlanPoint, polygon: readonly FloorPlanPoint[]): boolean {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const a = polygon[previous];
    const b = polygon[index];
    if (onSegment(point, a, b)) return true;
    if (
      (a.y > point.y) !== (b.y > point.y)
      && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    ) inside = !inside;
  }
  return inside;
}

function edges(polygon: readonly FloorPlanPoint[]): [FloorPlanPoint, FloorPlanPoint][] {
  return polygon.map((point, index) => [point, polygon[(index + 1) % polygon.length]]);
}

function polygonWithin(
  inner: readonly FloorPlanPoint[],
  outer: readonly FloorPlanPoint[],
): boolean {
  return inner.every((point) => pointInPolygon(point, outer))
    && !edges(inner).some(([a, b]) =>
      edges(outer).some(([c, d]) => properIntersection(a, b, c, d))
    );
}

function polygonsOverlap(a: readonly FloorPlanPoint[], b: readonly FloorPlanPoint[]): boolean {
  return edges(a).some(([a1, a2]) => edges(b).some(([b1, b2]) => segmentsIntersect(a1, a2, b1, b2)))
    || a.some((point) => pointInPolygon(point, b))
    || b.some((point) => pointInPolygon(point, a));
}

type PolygonExtent = { left: number; right: number; top: number; bottom: number; };

function polygonExtent(polygon: readonly FloorPlanPoint[]): PolygonExtent {
  let left = Infinity;
  let right = -Infinity;
  let top = Infinity;
  let bottom = -Infinity;
  for (const point of polygon) {
    left = Math.min(left, point.x);
    right = Math.max(right, point.x);
    top = Math.min(top, point.y);
    bottom = Math.max(bottom, point.y);
  }
  return { left, right, top, bottom };
}

/**
 * True when two extents cannot share interior space, whatever shape sits inside them. It is a
 * cheap pre-check before the full axis sweep, which most pairs in a large hall never need.
 * Extents that only touch count as apart, matching the axis sweep, so the `<=` is required.
 */
function extentsApart(a: PolygonExtent, b: PolygonExtent): boolean {
  return a.right <= b.left || b.right <= a.left || a.bottom <= b.top || b.bottom <= a.top;
}

/**
 * Separating-axis test for two convex footprints, where sharing an edge is not an overlap.
 * `polygonsOverlap` counts a touching edge as a hit, which is right for a clearance envelope but
 * wrong for occupancy: two tables laid flush against each other still occupy distinct floor.
 */
function convexInteriorsOverlap(
  a: readonly FloorPlanPoint[],
  b: readonly FloorPlanPoint[],
): boolean {
  for (const polygon of [a, b]) {
    for (const [start, end] of edges(polygon)) {
      const axis = { x: start.y - end.y, y: end.x - start.x };
      const length = Math.hypot(axis.x, axis.y);
      if (length === 0) continue;
      const project = (points: readonly FloorPlanPoint[]) => {
        let min = Infinity;
        let max = -Infinity;
        for (const point of points) {
          const value = (point.x * axis.x + point.y * axis.y) / length;
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
        return { min, max };
      };
      const first = project(a);
      const second = project(b);
      if (first.max - second.min <= EPSILON || second.max - first.min <= EPSILON) return false;
    }
  }
  return true;
}

/**
 * The same ring with its degenerate edges gone: a repeated closing point, or a corner clicked
 * twice, describe the same floor as the ring without them. Left in, each reads as an edge of no
 * length that every neighbouring edge "crosses", and a plain rectangle is condemned as
 * self-crossing - which is how a hall an Organizer had already drawn came back as no hall at all.
 */
function withoutDegenerateEdges(polygon: readonly FloorPlanPoint[]): readonly FloorPlanPoint[] {
  const kept = polygon.filter((point, index) => {
    const before = polygon[(index + polygon.length - 1) % polygon.length];
    return point.x !== before.x || point.y !== before.y;
  });
  return kept.length >= 3 ? kept : polygon;
}

function polygonIsSimple(raw: readonly FloorPlanPoint[]): boolean {
  const polygon = withoutDegenerateEdges(raw);
  const polygonEdges = edges(polygon);
  return polygonEdges.every(([a, b], index) =>
    polygonEdges.every(([c, d], other) => {
      if (
        index === other || (index + 1) % polygon.length === other
        || (other + 1) % polygon.length === index
      ) return true;
      return !segmentsIntersect(a, b, c, d);
    })
  );
}

export function placementIssues(table: FloorPlanTable, document: FloorPlanDocument): string[] {
  const envelope = envelopePolygon(table, document);
  const issues: string[] = [];
  if (!polygonWithin(envelope, document.boundary)) issues.push("boundary");
  for (const area of document.areas) {
    if (area.obstructs && polygonsOverlap(envelope, area.polygon)) issues.push(area.id);
  }
  return issues;
}

export type FloorPlanValidationIssue = { path: string; code: string; message: string; };

/**
 * Whether a polygon is something a plan can actually be built on. Counting points is not enough:
 * three collinear ones enclose no floor and a self-crossing outline has no inside, and
 * `validateFloorPlan` rejects both. Readiness and validation have to agree, or the editor lets an
 * Organizer fill a hall the document will not accept.
 */
export function isUsableBoundary(polygon: readonly FloorPlanPoint[]): boolean {
  return polygon.length >= 3 && polygonArea(polygon) !== 0 && polygonIsSimple(polygon);
}

export function validateFloorPlan(document: FloorPlanDocument): FloorPlanValidationIssue[] {
  const issues: FloorPlanValidationIssue[] = [];
  const validatePolygon = (polygon: FloorPlanPoint[], path: string) => {
    if (polygon.length < 3 || polygonArea(polygon) === 0) {
      issues.push({
        path,
        code: "area",
        message: "Polygon needs at least three non-collinear points.",
      });
    } else if (!polygonIsSimple(polygon)) {
      issues.push({ path, code: "self-intersection", message: "Polygon crosses itself." });
    }
  };
  validatePolygon(document.boundary, "boundary");
  document.areas.forEach((area, index) => {
    const path = `areas[${index}].polygon`;
    validatePolygon(area.polygon, path);
    if (document.boundary.length >= 3 && !polygonWithin(area.polygon, document.boundary)) {
      issues.push({ path, code: "boundary", message: "Area must stay inside the floor boundary." });
    }
  });
  const ids = new Set<string>();
  const numbers = new Set<string>();
  for (const [index, table] of document.tables.entries()) {
    if (ids.has(table.id)) {
      issues.push({
        path: `tables[${index}].id`,
        code: "duplicate",
        message: "Table ID must be unique.",
      });
    }
    ids.add(table.id);
    /*
     * A number is what a Vendor is told to find, so two tables cannot share one. Checked here
     * rather than only where it is typed, because import and paste reach the document too. A plan
     * that already holds duplicates still parses - it opens for repair rather than refusing.
     */
    const folded = table.number.trim().toLocaleLowerCase();
    if (numbers.has(folded)) {
      issues.push({
        path: `tables[${index}].number`,
        code: "duplicate",
        message: `Table number ${table.number} is used more than once.`,
      });
    }
    numbers.add(folded);
    for (const code of placementIssues(table, document)) {
      issues.push({
        path: `tables[${index}]`,
        code,
        message: code === "boundary"
          ? "Table clearance leaves the floor boundary."
          : `Table clearance crosses area ${code}.`,
      });
    }
  }
  return issues;
}

/** True when the polygon is four corners forming an axis-aligned, non-degenerate rectangle. */
export function isAxisAlignedRectangle(polygon: readonly FloorPlanPoint[]): boolean {
  if (polygon.length !== 4) return false;
  const xs = [...new Set(polygon.map((point) => point.x))];
  const ys = [...new Set(polygon.map((point) => point.y))];
  if (xs.length !== 2 || ys.length !== 2) return false;
  return polygon.every((point) => xs.includes(point.x) && ys.includes(point.y));
}

/**
 * The stock shapes an organizer drags out for a stage, a pillar, a bar or a food truck. Every one
 * resolves to a polygon, so obstruction, capacity and containment maths stay one code path; a
 * circle is a finely cut polygon, not a special case.
 */
export type FloorPlanShape = "rectangle" | "circle" | "triangle" | "parallelogram";

export const FLOOR_PLAN_SHAPES: readonly FloorPlanShape[] = [
  "rectangle",
  "circle",
  "triangle",
  "parallelogram",
];

/** Points around a circle's rim. Fine enough to read as round at any zoom the editor allows. */
const CIRCLE_POINTS = 48;

export function shapePolygon(
  shape: FloorPlanShape,
  from: FloorPlanPoint,
  to: FloorPlanPoint,
): FloorPlanPoint[] {
  const left = Math.round(Math.min(from.x, to.x));
  const right = Math.round(Math.max(from.x, to.x));
  const top = Math.round(Math.min(from.y, to.y));
  const bottom = Math.round(Math.max(from.y, to.y));
  const width = right - left;
  const height = bottom - top;
  if (width === 0 || height === 0) return [];

  if (shape === "rectangle") {
    return [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ];
  }
  if (shape === "triangle") {
    return [
      { x: Math.round(left + width / 2), y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ];
  }
  if (shape === "parallelogram") {
    const lean = Math.round(width / 4);
    return [
      { x: left + lean, y: top },
      { x: right, y: top },
      { x: right - lean, y: bottom },
      { x: left, y: bottom },
    ];
  }
  const centreX = left + width / 2;
  const centreY = top + height / 2;
  return Array.from({ length: CIRCLE_POINTS }, (_unused, index) => {
    const angle = (index / CIRCLE_POINTS) * 2 * Math.PI;
    return {
      x: Math.round(centreX + (width / 2) * Math.cos(angle)),
      y: Math.round(centreY + (height / 2) * Math.sin(angle)),
    };
  });
}

/** Axis-aligned rectangle from two dragged corners, clockwise from the top-left. */
export function rectangleBoundary(from: FloorPlanPoint, to: FloorPlanPoint): FloorPlanPoint[] {
  const left = Math.round(Math.min(from.x, to.x));
  const right = Math.round(Math.max(from.x, to.x));
  const top = Math.round(Math.min(from.y, to.y));
  const bottom = Math.round(Math.max(from.y, to.y));
  if (right === left || bottom === top) return [];
  return [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ];
}

/**
 * Scales the whole plan uniformly about the boundary's top-left extent until the room encloses
 * `squareFeet`. This is the plan's one editable scale (TRO-1276): getting it wrong here used to be
 * a one-way door, because rescaling the boundary alone left every table and area exactly where it
 * stood, adrift of the walls that had just moved under it. A table is a physical object whose
 * footprint comes from the real world, so it holds its size and only moves. An area was traced
 * against the same misjudged geometry this correction is undoing, so it moves AND resizes with
 * the room - which is the only way a rescale stays usable rather than forcing a redraw.
 */
export function resizeBoundaryToArea(
  document: FloorPlanDocument,
  squareFeet: number,
): FloorPlanDocument {
  if (document.boundary.length < 3 || !Number.isFinite(squareFeet) || squareFeet <= 0) {
    return document;
  }
  const current = polygonArea(document.boundary);
  if (current <= 0) return document;
  const factor = Math.sqrt((squareFeet * SIXTEENTHS_PER_FOOT ** 2) / current);
  if (!Number.isFinite(factor) || factor <= 0) return document;
  const originX = Math.min(...document.boundary.map((point) => point.x));
  const originY = Math.min(...document.boundary.map((point) => point.y));
  const rescale = (point: FloorPlanPoint): FloorPlanPoint => ({
    x: Math.round(originX + (point.x - originX) * factor),
    y: Math.round(originY + (point.y - originY) * factor),
  });
  return {
    ...document,
    boundary: document.boundary.map(rescale),
    areas: document.areas.map((area) => ({ ...area, polygon: area.polygon.map(rescale) })),
    tables: document.tables.map((table) => ({ ...table, center: rescale(table.center) })),
  };
}

export function addTable(
  document: FloorPlanDocument,
  center: FloorPlanPoint,
  rotation = 0,
): FloorPlanDocument {
  /*
   * A table outside every room is a table `validateFloorPlan` refuses, so a plan with no usable
   * boundary has nowhere to put one. The editor keeps the tool out of reach until a room exists,
   * but readiness can also be lost under an armed tool - an undo past the room, a revision handed
   * in from outside - and the document is the one place that holds whatever the editor misses.
   */
  if (!isUsableBoundary(document.boundary)) return document;
  const next = tableAllocator(document).take();
  const upright = rotation % 180 === 0;
  const spanX = upright ? document.defaults.table.width : document.defaults.table.depth;
  const spanY = upright ? document.defaults.table.depth : document.defaults.table.width;
  return {
    ...document,
    tables: [...document.tables, {
      ...next,
      center: {
        x: edgeAlignedCenter(Math.round(center.x), spanX),
        y: edgeAlignedCenter(Math.round(center.y), spanY),
      },
      width: document.defaults.table.width,
      depth: document.defaults.table.depth,
      rotation,
    }],
  };
}

export function moveTables(
  document: FloorPlanDocument,
  ids: readonly string[],
  delta: FloorPlanPoint,
): FloorPlanDocument {
  const moving = new Set(ids);
  if (!moving.size || (delta.x === 0 && delta.y === 0)) return document;
  if (!document.tables.some((table) => moving.has(table.id))) return document;
  return {
    ...document,
    tables: document.tables.map((table) =>
      moving.has(table.id)
        ? {
          ...table,
          center: {
            x: Math.round(table.center.x + delta.x),
            y: Math.round(table.center.y + delta.y),
          },
        }
        : table
    ),
  };
}

/** Copies tables at an offset and renumbers the copies; returns the plan and the new ids. */
export function duplicateTables(
  document: FloorPlanDocument,
  ids: readonly string[],
  offset: FloorPlanPoint,
): { document: FloorPlanDocument; ids: string[]; } {
  const wanted = new Set(ids);
  const source = document.tables.filter((table) => wanted.has(table.id));
  if (!source.length) return { document, ids: [] };
  const allocate = tableAllocator(document);
  const copies = source.map((table): FloorPlanTable => ({
    ...table,
    ...allocate.take(),
    center: {
      x: Math.round(table.center.x + offset.x),
      y: Math.round(table.center.y + offset.y),
    },
  }));
  return {
    document: { ...document, tables: [...document.tables, ...copies] },
    ids: copies.map((table) => table.id),
  };
}

export function moveAreas(
  document: FloorPlanDocument,
  ids: readonly string[],
  delta: FloorPlanPoint,
): FloorPlanDocument {
  const moving = new Set(ids);
  if (!moving.size || (delta.x === 0 && delta.y === 0)) return document;
  if (!document.areas.some((area) => moving.has(area.id))) return document;
  return {
    ...document,
    areas: document.areas.map((area) =>
      moving.has(area.id)
        ? {
          ...area,
          polygon: area.polygon.map((point) => ({
            x: Math.round(point.x + delta.x),
            y: Math.round(point.y + delta.y),
          })),
        }
        : area
    ),
  };
}

export function removeAreas(
  document: FloorPlanDocument,
  ids: readonly string[],
): FloorPlanDocument {
  const removing = new Set(ids);
  if (!removing.size) return document;
  const areas = document.areas.filter((area) => !removing.has(area.id));
  return areas.length === document.areas.length ? document : { ...document, areas };
}

export function updateArea(
  document: FloorPlanDocument,
  id: string,
  patch: Partial<Pick<FloorPlanArea, "name" | "obstructs">>,
): FloorPlanDocument {
  if (!document.areas.some((area) => area.id === id)) return document;
  return {
    ...document,
    areas: document.areas.map((area) => area.id === id ? { ...area, ...patch } : area),
  };
}

/**
 * Reshapes an area by moving one of its own corners - the direct-manipulation way to resize it,
 * as opposed to `moveAreas`, which carries the whole shape. Unlike a table, an area has no fixed
 * width/depth to snap to the foot; it is free-form, so the only rule is that a corner may not land
 * on top of its own neighbour and erase an edge.
 */
export function moveAreaVertex(
  document: FloorPlanDocument,
  id: string,
  index: number,
  point: FloorPlanPoint,
): FloorPlanDocument {
  const area = document.areas.find((candidate) => candidate.id === id);
  if (!area || index < 0 || index >= area.polygon.length) return document;
  if (samePoint(point, area.polygon[index])) return document;
  const polygon = reshapedAreaPolygon(area.polygon, index, point);
  // Two corners landing on top of each other is a collapsed edge for a traced shape, and a zero
  // width or depth for a rectangle - refused either way, the same way a table's own resize never
  // lets a dimension reach zero.
  if (
    polygon.some((corner, at) =>
      polygon.some((other, otherAt) => otherAt !== at && samePoint(corner, other))
    )
  ) {
    return document;
  }
  return {
    ...document,
    areas: document.areas.map((candidate) =>
      candidate.id === id ? { ...candidate, polygon } : candidate
    ),
  };
}

/**
 * What moving one corner of an area does to the rest of its polygon. A traced, free-form shape
 * moves only the corner given - there is no "opposite side" to keep in step. An axis-aligned
 * rectangle keeps its own shape instead of skewing into an arbitrary quadrilateral: the two edges
 * that meet at the moved corner slide with it, the opposite corner stays exactly where it was, the
 * same way dragging a rectangle's corner works in any drawing tool.
 */
export function reshapedAreaPolygon(
  polygon: readonly FloorPlanPoint[],
  index: number,
  point: FloorPlanPoint,
): FloorPlanPoint[] {
  if (!isAxisAlignedRectangle(polygon)) {
    return polygon.map((corner, at) => at === index ? point : corner);
  }
  const previousIndex = (index + polygon.length - 1) % polygon.length;
  const nextIndex = (index + 1) % polygon.length;
  const original = polygon[index];
  const previous = polygon[previousIndex];
  const next = polygon[nextIndex];
  const reshaped = [...polygon];
  reshaped[index] = point;
  reshaped[previousIndex] = previous.x === original.x
    ? { x: point.x, y: previous.y }
    : { x: previous.x, y: point.y };
  reshaped[nextIndex] = next.x === original.x
    ? { x: point.x, y: next.y }
    : { x: next.x, y: point.y };
  return reshaped;
}

function samePoint(a: FloorPlanPoint, b: FloorPlanPoint): boolean {
  return a.x === b.x && a.y === b.y;
}

/** Copies areas at an offset, giving each copy a fresh id; returns the plan and the new ids. */
export function duplicateAreas(
  document: FloorPlanDocument,
  ids: readonly string[],
  offset: FloorPlanPoint,
): { document: FloorPlanDocument; ids: string[]; } {
  const wanted = new Set(ids);
  const source = document.areas.filter((area) => wanted.has(area.id));
  if (!source.length) return { document, ids: [] };
  let number = document.areas.length + 1;
  const taken = new Set(document.areas.map((area) => area.id));
  const copies = source.map((area): FloorPlanArea => {
    while (taken.has(`area-${number}`)) number += 1;
    const copy: FloorPlanArea = {
      ...area,
      id: `area-${number}`,
      polygon: area.polygon.map((point) => ({
        x: Math.round(point.x + offset.x),
        y: Math.round(point.y + offset.y),
      })),
    };
    taken.add(copy.id);
    return copy;
  });
  return {
    document: { ...document, areas: [...document.areas, ...copies] },
    ids: copies.map((area) => area.id),
  };
}

export function removeTables(
  document: FloorPlanDocument,
  ids: readonly string[],
): FloorPlanDocument {
  const removing = new Set(ids);
  if (!removing.size) return document;
  const tables = document.tables.filter((table) => !removing.has(table.id));
  return tables.length === document.tables.length ? document : { ...document, tables };
}

/**
 * Fills the room the way an organizer lays out a hall: rows in facing pairs, alternating which
 * way the vendor stands. Consecutive rows share the clearance between them: two rows facing each
 * other share one `front` walkway, and two rows back-to-back share one `back` vendor strip, as in
 * real plans. Tables that would leave the boundary, land on an obstructing area, or sit on floor
 * an existing table occupies are skipped, so every placed table is legal and a second fill
 * extends the layout instead of stacking a copy on top.
 */
export function autoFillTables(document: FloorPlanDocument, count: number): FloorPlanDocument {
  if (document.boundary.length < 3 || !Number.isInteger(count) || count <= 0) return document;
  const { table: defaults, clearance } = document.defaults;
  // Rows here are always axis-aligned, so they take the same gridded pitch a dragged row takes.
  const { pitch: across, lead } = griddedRowPitch(document);
  if (across <= 0 || defaults.depth <= 0) return document;
  const left = Math.min(...document.boundary.map((point) => point.x));
  const right = Math.max(...document.boundary.map((point) => point.x));
  const top = Math.min(...document.boundary.map((point) => point.y));
  const bottom = Math.max(...document.boundary.map((point) => point.y));

  const allocate = tableAllocator(document);
  const added: FloorPlanTable[] = [];
  // Floor the plan has already spent. A taken slot is skipped, so a second fill continues past
  // the tables the first one laid. Extents are kept so a candidate is only swept against
  // footprints it could actually meet.
  const occupied = document.tables.map((placed) => {
    const polygon = tablePolygon(placed);
    return { polygon, extent: polygonExtent(polygon) };
  });
  let y = top + clearance.back;
  let rotation = 0;
  while (y + defaults.depth <= bottom && added.length < count) {
    const centerY = edgeAlignedCenter(Math.round(y + defaults.depth / 2), defaults.depth);
    for (let x = left + lead; x <= right && added.length < count; x += across) {
      const candidate: FloorPlanTable = {
        ...allocate.peek(),
        center: { x: edgeAlignedCenter(Math.round(x), defaults.width), y: centerY },
        width: defaults.width,
        depth: defaults.depth,
        rotation,
      };
      if (placementIssues(candidate, document).length) continue;
      const footprint = tablePolygon(candidate);
      const extent = polygonExtent(footprint);
      const taken = occupied.some((placed) =>
        !extentsApart(extent, placed.extent) && convexInteriorsOverlap(footprint, placed.polygon)
      );
      if (taken) continue;
      occupied.push({ polygon: footprint, extent });
      added.push({ ...candidate, ...allocate.take() });
    }
    // A row facing +y meets the next row front-to-front across one shared walkway; a row facing
    // -y meets it back-to-back across one shared vendor strip.
    y += defaults.depth + (rotation === 0 ? clearance.front : clearance.back);
    rotation = rotation === 0 ? 180 : 0;
  }
  if (!added.length) return document;
  return { ...document, tables: [...document.tables, ...added] };
}

export function addArea(document: FloorPlanDocument, area: FloorPlanArea): FloorPlanDocument {
  /*
   * An area outside every room is one `validateFloorPlan` refuses, exactly as it refuses a table
   * there, so a plan with no usable boundary has nowhere to put one either. The editor keeps the
   * tool out of reach until a room exists, but readiness can be lost under an armed tool, and the
   * document is the one place that holds whatever the editor misses.
   */
  if (!isUsableBoundary(document.boundary)) return document;
  return { ...document, areas: [...document.areas, area] };
}

/**
 * Hands out the id and the display number for tables about to be added.
 *
 * The two are derived separately. An id must be unique within this document, so it is the first
 * `table-N` no table holds, the same walk `nextAreaId` does. A number is the free-form label an
 * Organizer reads off the floor, and it continues from the highest one that parses as a count.
 * Deriving the id from the number would reuse an id in a plan numbered "A1", "A2", "A3", which
 * `parseFloorPlan` accepts.
 *
 * `peek` is idempotent: auto-fill names a candidate before it knows whether the floor is free,
 * and a rejected slot must not use up the id.
 */
function tableAllocator(document: FloorPlanDocument): {
  peek: () => { id: string; number: string; };
  take: () => { id: string; number: string; };
} {
  const taken = new Set(document.tables.map((table) => table.id));
  const counts = document.tables.map((table) => Number(table.number)).filter(Number.isFinite);
  let number = counts.length ? Math.max(...counts) + 1 : 1;
  let index = 1;
  const peek = () => {
    while (taken.has(`table-${index}`)) index += 1;
    return { id: `table-${index}`, number: String(number) };
  };
  return {
    peek,
    take: () => {
      const next = peek();
      taken.add(next.id);
      number += 1;
      return next;
    },
  };
}

/** Row angles the drag rounds to, so a row meant to be straight is straight. */
export const ROW_ANGLE_STEP = 15;

/**
 * A row's angle, rounded to `ROW_ANGLE_STEP`. Freehand drags are never exactly axis-aligned,
 * and a row that is a fraction of a degree off reads as a mistake at hall scale.
 */
export function snapRowAngle(degrees: number): number {
  return Math.round(degrees / ROW_ANGLE_STEP) * ROW_ANGLE_STEP;
}

/**
 * Snaps so a shape's edges, not its center, land on the foot grid (TRO-546). A 3ft-deep table
 * centered on a whole foot has its edges on half-feet, so the center carries the remainder of
 * `extent / 2` for the edges to come out flush. For an even number of feet the offset is zero and
 * this is a plain snap.
 *
 * It applies to any extent, including custom depths that are not a whole number of feet. Such a
 * depth cannot put both edges on the grid, but carrying the remainder still lands one edge flush
 * and leaves the other only the depth's own remainder away. Skipping alignment would leave both
 * edges an arbitrary distance off the grid.
 */
export function edgeAlignedCenter(center: number, extent: number): number {
  const half = extent / 2;
  const offset = ((half % SIXTEENTHS_PER_FOOT) + SIXTEENTHS_PER_FOOT) % SIXTEENTHS_PER_FOOT;
  return Math.round((center - offset) / SIXTEENTHS_PER_FOOT) * SIXTEENTHS_PER_FOOT + offset;
}

/**
 * The across-the-row pitch and lead-in for an axis-aligned row, rounded so every center lands on
 * the foot grid. It rounds up because the side clearance it derives from is a safety minimum.
 * Dragging a row and filling the hall both read it, so they agree on column spacing.
 */
export function griddedRowPitch(
  document: FloorPlanDocument,
): { pitch: number; lead: number; raw: number; } {
  const raw = document.defaults.table.width + 2 * document.defaults.clearance.side;
  const pitch = Math.ceil(raw / SIXTEENTHS_PER_FOOT) * SIXTEENTHS_PER_FOOT;
  return { pitch, lead: Math.round(pitch / 2 / SIXTEENTHS_PER_FOOT) * SIXTEENTHS_PER_FOOT, raw };
}

/**
 * How two table numbers are compared for sameness: trimmed and case-folded, so `A1` and `a1` are
 * one number. The Organizer's own casing is never rewritten - only the comparison folds.
 */
export function sameTableNumber(one: string, other: string): boolean {
  return one.trim().toLocaleLowerCase() === other.trim().toLocaleLowerCase();
}

/** Whether some OTHER table already answers to this number. */
export function tableNumberTaken(
  document: FloorPlanDocument,
  number: string,
  exceptId: string,
): boolean {
  return document.tables.some((table) =>
    table.id !== exceptId && sameTableNumber(table.number, number)
  );
}

/**
 * Renames a table, or returns the document untouched when the name cannot stand: blank, or one
 * another table already answers to. Callers compare by identity to know it was refused.
 */
export function renameTable(
  document: FloorPlanDocument,
  id: string,
  number: string,
): FloorPlanDocument {
  const wanted = number.trim();
  if (!wanted || tableNumberTaken(document, wanted, id)) return document;
  if (!document.tables.some((table) => table.id === id)) return document;
  return {
    ...document,
    tables: document.tables.map((table) => table.id === id ? { ...table, number: wanted } : table),
  };
}

/** The world rectangle a set of tables occupies, or null when none of them are in the plan. */
export function tablesBounds(
  document: FloorPlanDocument,
  ids: readonly string[],
): { x: number; y: number; width: number; height: number; } | null {
  const corners = document.tables
    .filter((table) => ids.includes(table.id))
    .flatMap(tablePolygon);
  if (!corners.length) return null;
  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

/**
 * Tiles a selection along one axis. The selection is the unit that repeats, so a pair laid out
 * back to back repeats as a pair, and every clone is a new table with its own id and number -
 * two tables sharing a number is the one thing a plan may never hold.
 */
export function repeatTables(
  document: FloorPlanDocument,
  ids: readonly string[],
  axis: "x" | "y",
  copies: number,
  pitch: number,
): FloorPlanDocument {
  const unit = document.tables.filter((table) => ids.includes(table.id));
  // A negative pitch is a row marching the other way, not a refusal; only a zero one means
  // every copy would land on top of the thing it was copied from.
  if (!unit.length || copies < 1 || pitch === 0) return document;
  const allocator = tableAllocator(document);
  const made: FloorPlanTable[] = [];
  for (let copy = 1; copy <= copies; copy += 1) {
    const shift = Math.round(pitch * copy);
    for (const table of unit) {
      const next = allocator.take();
      made.push({
        ...table,
        ...next,
        center: {
          x: table.center.x + (axis === "x" ? shift : 0),
          y: table.center.y + (axis === "y" ? shift : 0),
        },
      });
    }
  }
  return { ...document, tables: [...document.tables, ...made] };
}

export function rotateTable(
  document: FloorPlanDocument,
  id: string,
  degrees: number,
): FloorPlanDocument {
  return {
    ...document,
    tables: document.tables.map((table) =>
      table.id === id ? { ...table, rotation: table.rotation + degrees } : table
    ),
  };
}

/**
 * Turns a group of tables clockwise as one body: each table's own rotation advances by `degrees`
 * and its center orbits the group's centroid, so a row of twenty swings round rather than each
 * table spinning in place. One table orbits itself, which is `rotateTable`.
 *
 * Centers are rounded to whole sixteenths because the document stores integers (`|| 0` keeps a
 * rounded -0 out of the stored coordinate). Out-of-bounds
 * results are left for `validateFloorPlan` to mark, as for any other move.
 */
export function rotateTables(
  document: FloorPlanDocument,
  ids: readonly string[],
  degrees: number,
): FloorPlanDocument {
  const wanted = new Set(ids);
  const group = document.tables.filter((table) => wanted.has(table.id));
  if (!group.length) return document;
  const around = {
    x: group.reduce((sum, table) => sum + table.center.x, 0) / group.length,
    y: group.reduce((sum, table) => sum + table.center.y, 0) / group.length,
  };
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    ...document,
    tables: document.tables.map((table) => {
      if (!wanted.has(table.id)) return table;
      const dx = table.center.x - around.x;
      const dy = table.center.y - around.y;
      return {
        ...table,
        center: {
          x: Math.round(around.x + dx * cos - dy * sin) || 0,
          y: Math.round(around.y + dx * sin + dy * cos) || 0,
        },
        rotation: table.rotation + degrees,
      };
    }),
  };
}

/**
 * Mirrors a group of tables about its own center: `horizontal` swaps left and right, `vertical`
 * swaps top and bottom. Each table's facing mirrors with it, which is the point: a row built
 * against one wall, copied and flipped top to bottom, faces out from the opposite wall.
 *
 * Rotation is clockwise with zero facing +y, so a left-right mirror negates it and a top-bottom
 * mirror takes it to 180 minus itself. Rounding and `|| 0` are as for `rotateTables`.
 */
export function flipTables(
  document: FloorPlanDocument,
  ids: readonly string[],
  axis: "horizontal" | "vertical",
): FloorPlanDocument {
  const wanted = new Set(ids);
  const group = document.tables.filter((table) => wanted.has(table.id));
  if (!group.length) return document;
  const xs = group.map((table) => table.center.x);
  const ys = group.map((table) => table.center.y);
  const midX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const midY = (Math.min(...ys) + Math.max(...ys)) / 2;
  const wrap = (degrees: number) => ((degrees % 360) + 360) % 360;
  return {
    ...document,
    tables: document.tables.map((table) => {
      if (!wanted.has(table.id)) return table;
      return axis === "horizontal"
        ? {
          ...table,
          center: { x: Math.round(2 * midX - table.center.x) || 0, y: table.center.y },
          rotation: wrap(-table.rotation),
        }
        : {
          ...table,
          center: { x: table.center.x, y: Math.round(2 * midY - table.center.y) || 0 },
          rotation: wrap(180 - table.rotation),
        };
    }),
  };
}

/**
 * A table's clearance override, or the plan's own if it has never been given one. The one fallback
 * `envelopePolygon` and `narrowestAisle` already used inline; named so the inspector panel that
 * writes it (TRO-1283) reads it the same way.
 */
export function resolvedClearance(
  table: FloorPlanTable,
  document: FloorPlanDocument,
): FloorPlanClearance {
  return table.clearance ?? document.defaults.clearance;
}

/**
 * Resizes placed tables directly, around their own center. A table owns its dimensions once
 * placed (TRO-1282) - nothing else touches width or depth - so this is the one path that changes
 * them after the fact. Position, rotation, numbering and Vendor assignment (kept elsewhere, keyed
 * by table id) are untouched.
 */
export function resizeTables(
  document: FloorPlanDocument,
  ids: readonly string[],
  patch: { width?: number; depth?: number; },
): FloorPlanDocument {
  const wanted = new Set(ids);
  if (!wanted.size || !document.tables.some((table) => wanted.has(table.id))) return document;
  if (patch.width !== undefined && (!Number.isInteger(patch.width) || patch.width <= 0)) {
    return document;
  }
  if (patch.depth !== undefined && (!Number.isInteger(patch.depth) || patch.depth <= 0)) {
    return document;
  }
  return {
    ...document,
    tables: document.tables.map((table) =>
      wanted.has(table.id)
        ? { ...table, width: patch.width ?? table.width, depth: patch.depth ?? table.depth }
        : table
    ),
  };
}

/**
 * Overrides the clearance around placed tables - the walkway, vendor space and side gap TRO-1283
 * asks for. Only the given sides change; the rest keep whatever the table already had, falling
 * back to the plan's own defaults the same way `resolvedClearance` does.
 */
export function respaceTables(
  document: FloorPlanDocument,
  ids: readonly string[],
  patch: Partial<FloorPlanClearance>,
): FloorPlanDocument {
  const wanted = new Set(ids);
  if (!wanted.size || !document.tables.some((table) => wanted.has(table.id))) return document;
  const given = Object.values(patch).filter((value) => value !== undefined);
  if (given.some((value) => !Number.isInteger(value) || value < 0)) return document;
  return {
    ...document,
    tables: document.tables.map((table) =>
      wanted.has(table.id)
        ? { ...table, clearance: { ...resolvedClearance(table, document), ...patch } }
        : table
    ),
  };
}

/**
 * The deliberate action TRO-1282 asks for. A placed table owns its dimensions - nothing resizes
 * under an Organizer's feet just because "Table pitch" changed - so that panel is only a template
 * for new tables until this runs. Resets every placed table to the plan's current defaults, width,
 * depth and spacing alike, while position, numbering and Vendor assignment (none of them part of a
 * table's geometry) survive untouched.
 */
export function applyTableDefaultsToAll(document: FloorPlanDocument): FloorPlanDocument {
  if (!document.tables.length) return document;
  return {
    ...document,
    tables: document.tables.map((table) => ({
      ...table,
      width: document.defaults.table.width,
      depth: document.defaults.table.depth,
      clearance: undefined,
    })),
  };
}

export type FloorPlanCapacity = {
  grossArea: number;
  netArea: number;
  pitchArea: number;
  noAislesEstimate: number;
  placed: number;
  illegalTableIds: string[];
};

export function capacity(document: FloorPlanDocument): FloorPlanCapacity {
  const grossArea = polygonArea(document.boundary);
  const obstructedArea = document.areas
    .filter((area) => area.obstructs)
    .reduce((total, area) => total + polygonArea(area.polygon), 0);
  const netArea = Math.max(0, grossArea - obstructedArea);
  const defaultTable = document.defaults.table;
  const clearance = document.defaults.clearance;
  const pitchArea = (defaultTable.width + 2 * clearance.side)
    * (defaultTable.depth + clearance.front + clearance.back);
  return {
    grossArea,
    netArea,
    pitchArea,
    noAislesEstimate: Math.floor(netArea / pitchArea),
    placed: document.tables.length,
    illegalTableIds: document.tables
      .filter((placedTable) => placementIssues(placedTable, document).length > 0)
      .map((placedTable) => placedTable.id),
  };
}

/** Narrowest table-top gap where two rows' facing approach zones overlap laterally. */
export function narrowestAisle(document: FloorPlanDocument): number | null {
  let narrowest = Infinity;
  for (let firstIndex = 0; firstIndex < document.tables.length; firstIndex += 1) {
    const first = document.tables[firstIndex];
    const radians = (first.rotation * Math.PI) / 180;
    const front = { x: -Math.sin(radians), y: Math.cos(radians) };
    const across = { x: Math.cos(radians), y: Math.sin(radians) };
    for (let secondIndex = firstIndex + 1; secondIndex < document.tables.length; secondIndex += 1) {
      const second = document.tables[secondIndex];
      const secondRadians = (second.rotation * Math.PI) / 180;
      const secondFront = { x: -Math.sin(secondRadians), y: Math.cos(secondRadians) };
      if (front.x * secondFront.x + front.y * secondFront.y > -0.99) continue;
      const delta = { x: second.center.x - first.center.x, y: second.center.y - first.center.y };
      const forward = delta.x * front.x + delta.y * front.y;
      if (forward <= 0) continue;
      const lateral = Math.abs(delta.x * across.x + delta.y * across.y);
      const firstSide = resolvedClearance(first, document).side;
      const secondSide = resolvedClearance(second, document).side;
      if (lateral > first.width / 2 + firstSide + second.width / 2 + secondSide) continue;
      narrowest = Math.min(narrowest, forward - first.depth / 2 - second.depth / 2);
    }
  }
  return Number.isFinite(narrowest) ? Math.max(0, Math.round(narrowest)) : null;
}

export type FloorPlanUnderlayCalibration = {
  sourceOrigin: FloorPlanPoint;
  worldOrigin: FloorPlanPoint;
  sixteenthsPerSourceUnit: number;
  /**
   * The reference line actually measured to produce this calibration, in source (image pixel)
   * space rather than world space - stable across a recalibration, which is precisely the moment
   * world space is about to change. Recalibrating reopens on this exact line and the exact length
   * typed for it, rather than a generic default an Organizer never drew. Optional: absent for a
   * provisional, never-explicitly-calibrated image, or one calibrated before this field existed.
   */
  reference?: { from: FloorPlanPoint; to: FloorPlanPoint; length: number; };
};

type UnderlayGeometry = {
  name: string;
  mediaType: "image/svg+xml" | "application/pdf" | "image/png" | "image/jpeg";
  sourceWidth: number;
  sourceHeight: number;
  calibration: FloorPlanUnderlayCalibration;
  calibrated: boolean;
};

export type FloorPlanUnderlayMetadata = Omit<UnderlayGeometry, "calibrated"> & {
  objectKey: string;
  calibrated: true;
};
/**
 * A background image as the editor holds it. `id`, `layerIndex` and `opacity` are live authoring
 * state rather than document facts: only the calibrated geometry in `FloorPlanUnderlayMetadata`
 * is persisted, so several images can be composed on screen without a schema change.
 */
export type FloorPlanUnderlay = UnderlayGeometry & {
  uri: string;
  objectKey?: string;
  id?: string;
  layerIndex?: number;
  opacity?: number;
};

const UNDERLAY_MEDIA: ReadonlySet<string> = new Set<FloorPlanUnderlay["mediaType"]>([
  "image/svg+xml",
  "application/pdf",
  "image/png",
  "image/jpeg",
]);

export function isSupportedUnderlay(mediaType: string, name: string): boolean {
  if (UNDERLAY_MEDIA.has(mediaType)) return true;
  return /\.(?:svg|pdf|png|jpe?g)$/i.test(name);
}

/** Underlays remain separate assets; only their source-to-document calibration crosses the seam. */
export function calibrateUnderlay(
  from: FloorPlanPoint,
  to: FloorPlanPoint,
  knownLength: number,
  worldOrigin: FloorPlanPoint = { x: 0, y: 0 },
): FloorPlanUnderlayCalibration {
  const sourceLength = Math.hypot(to.x - from.x, to.y - from.y);
  if (!Number.isInteger(knownLength) || knownLength <= 0 || sourceLength === 0) {
    throw new TypeError("Calibration needs two distinct points and a positive exact length.");
  }
  return {
    sourceOrigin: { ...from },
    worldOrigin: { ...worldOrigin },
    sixteenthsPerSourceUnit: knownLength / sourceLength,
    reference: { from: { ...from }, to: { ...to }, length: knownLength },
  };
}
